import React from "react";
import { useI18n } from "../lib/i18n.jsx";
import { Input } from "./ui/input";

export const MAX_DAYS = 7;
export const iso = (d) => d.toISOString().slice(0, 10);
export const shift = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return iso(d); };
export const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
export const presetRange = (p) => p === "yesterday" ? [shift(-1), shift(-1)] : p === "7d" ? [shift(-6), shift(0)] : [shift(0), shift(0)];

export default function ReportFilters({ period, setPeriod, status, setStatus, extra, setExtra, employees = [], projects = [], teams = [] }) {
  const { t, tn } = useI18n();
  const nm = (x) => (x && typeof x === "object" ? tn(x) : x);
  const tooLong = daysBetween(period.from, period.to) > MAX_DAYS;
  const pick = (p) => { const [from, to] = presetRange(p); setPeriod({ preset: p, from, to }); };
  const chip = (active) => `px-3 py-1.5 rounded-full text-sm font-semibold ${active ? "bg-[var(--sidebar)] text-white" : "tactical-card"}`;
  const sel = "h-9 border border-[var(--border)] rounded-md px-2 text-sm bg-[var(--card)] max-w-full";
  return (
    <div className="space-y-2 mb-3" data-testid="report-filters">
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs uppercase font-semibold text-[var(--text-3)] mr-1">{t("period")}</span>
        {["today", "yesterday", "7d", "custom"].map((p) => (
          <button key={p} data-testid={`period-${p}`} onClick={() => (p === "custom" ? setPeriod({ ...period, preset: "custom" }) : pick(p))} className={chip(period.preset === p)}>{t(`period_${p}`)}</button>
        ))}
        {period.preset === "custom" && (<>
          <Input data-testid="period-from" type="date" value={period.from} max={period.to} onChange={(e) => setPeriod({ ...period, from: e.target.value })} className="h-9 w-auto" />
          <Input data-testid="period-to" type="date" value={period.to} min={period.from} onChange={(e) => setPeriod({ ...period, to: e.target.value })} className="h-9 w-auto" />
        </>)}
      </div>
      {tooLong && <div data-testid="period-too-long" className="text-sm font-semibold text-[var(--red)]">{t("max_period_reports")}</div>}
      <div className="flex gap-2 flex-wrap">
        {["", "submitted", "in_review", "approved", "rejected", "draft"].map((s) => (
          <button key={s} data-testid={`filter-${s || "all"}`} onClick={() => setStatus(s)} className={chip(status === s)}>{s ? t(s) : t("all_statuses")}</button>
        ))}
      </div>
      {(employees.length > 0 || projects.length > 0 || teams.length > 0) && (
        <div className="flex gap-2 flex-wrap">
          {employees.length > 0 && <select data-testid="filter-employee" value={extra.employee_id} onChange={(e) => setExtra({ ...extra, employee_id: e.target.value })} className={sel}>
            <option value="">{t("employee")}: {t("all")}</option>{employees.map((e) => <option key={e.id} value={e.id}>{nm(e.name)}</option>)}</select>}
          {projects.length > 0 && <select data-testid="filter-project" value={extra.project_id} onChange={(e) => setExtra({ ...extra, project_id: e.target.value })} className={sel}>
            <option value="">{t("project")}: {t("all")}</option>{projects.map((p) => <option key={p.id} value={p.id}>{nm(p.name)}</option>)}</select>}
          {teams.length > 0 && <select data-testid="filter-team" value={extra.team_id} onChange={(e) => setExtra({ ...extra, team_id: e.target.value })} className={sel}>
            <option value="">{t("team")}: {t("all")}</option>{teams.map((tm) => <option key={tm.id} value={tm.id}>{nm(tm.name)}</option>)}</select>}
        </div>
      )}
    </div>
  );
}

export function Pager({ page, pageSize, total, onPage }) {
  const { t, tp } = useI18n();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1, to = Math.min(total, page * pageSize);
  const nums = [...new Set([1, page - 1, page, page + 1, pages].filter((n) => n >= 1 && n <= pages))];
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap mt-3 text-sm" data-testid="pager">
      <span className="text-[var(--text-3)]" data-testid="pager-summary">{tp("shown_of", { from, to, total })}</span>
      <div className="flex gap-1 items-center">
        <button data-testid="pager-prev" disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-3 h-9 rounded-md tactical-card disabled:opacity-40">{t("prev")}</button>
        {nums.map((n, i) => (<React.Fragment key={n}>
          {i > 0 && nums[i - 1] !== n - 1 && <span className="px-1">…</span>}
          <button data-testid={`pager-${n}`} onClick={() => onPage(n)} className={`w-9 h-9 rounded-md font-semibold ${n === page ? "bg-[var(--sidebar)] text-white" : "tactical-card"}`}>{n}</button>
        </React.Fragment>))}
        <button data-testid="pager-next" disabled={page >= pages} onClick={() => onPage(page + 1)} className="px-3 h-9 rounded-md tactical-card disabled:opacity-40">{t("next")}</button>
      </div>
    </div>
  );
}
